import React, { useState, useMemo, useEffect } from 'react';
import { TextField, MenuItem, Box, Typography } from '@mui/material';
import Material from './Material';

const StepThree = ({ supplierData, onSupplierDataChange }) => {
  const [formData, setFormData] = useState(
    supplierData.map(() => ({
      moduleType: '',
      cellSize: '',
      cellType: '',
      numberOfCells: '',
      moduleSize: '',
      mountingHoles: '',
      moduleTechnology: '',
      moduleRatings: 1,
      moduleWattageData: [
        { moduleWattageWp: '', moduleWattageNos: '', binningOption: '' },
      ], 
      materials: [],
    }))
  );

  useEffect(() => {
    onSupplierDataChange(formData); // Send updated form data back to the parent
  }, [formData, onSupplierDataChange]);

  const moduleTypeOptions = useMemo(
    () => [
      { value: 'Mono-Facial', label: 'Mono-Facial' },
      { value: 'Bi-Facial', label: 'Bi-Facial' },
    ],
    []
  );

  const binningOptions = useMemo(
    () => [
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
    ],
    []
  );

  const handleInputChange = (supplierIndex, event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => {
      const updatedFormData = [...prevFormData];
      updatedFormData[supplierIndex][name] = value; // allow free typing
      return updatedFormData;
    });
  };

  const handleRatingBlur = (supplierIndex, event) => {
    let value = parseInt(event.target.value, 10);
    if (isNaN(value) || value < 1) value = 1;
    if (value > 5) value = 5;

    setFormData((prevFormData) => {
      const updatedFormData = [...prevFormData];
      updatedFormData[supplierIndex].moduleRatings = value;

      updatedFormData[supplierIndex].moduleWattageData = Array.from(
        { length: value },
        (_, i) => updatedFormData[supplierIndex].moduleWattageData[i] || { moduleWattageWp: '', moduleWattageNos: '', binningOption: '' }
      );
      return updatedFormData;
    });
  };
  
  const handleMaterialChange = (supplierIndex, updatedMaterials) => {
    const updatedFormData = [...formData];
    updatedFormData[supplierIndex].materials = updatedMaterials;
    setFormData(updatedFormData);
  };

  const handleWattageChange = (supplierIndex, wattageIndex, event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => {
      const updatedFormData = [...prevFormData];
      updatedFormData[supplierIndex].moduleWattageData[wattageIndex][name] = value;
      return updatedFormData;
    });
  };

  const renderBinningFields = (binningOption, wattageData, handleChange) => {
    const fields = [];
    const count = parseInt(binningOption, 10);

    for (let i = 1; i <= count; i++) {
      fields.push(
        <React.Fragment key={i}>
          <Box display="grid" mt={2} gridTemplateColumns="repeat(2, 1fr)" gap={2}>
            <TextField
              label={`I${i}: Current Binning`}
              name={`i${i}CurrentBinning`}
              value={wattageData[`i${i}CurrentBinning`] || ''}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label={`I${i} Storage Location`}
              name={`i${i}Location`}
              value={wattageData[`i${i}Location`] || ''}
              onChange={handleChange}
              fullWidth
            />
          </Box>
        </React.Fragment>
      );
    }

    return fields;
  };

  return (
    <div>
      {supplierData.map((supplier, supplierIndex) => (
        <Box key={supplierIndex} mb={4} p={2}  borderRadius="8px"
        className="bg-gray-50 p-4 rounded-lg mt-3"
        style={{ boxShadow: 'inset 0px 0px 4px 2px rgb(0 0 0 / 0.05)' }}>
          <Typography variant="h6" gutterBottom>
            {`Supplier: ${supplier.name}`}
          </Typography>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <TextField
              select
              label="Module Type"
              name="moduleType"
              value={formData[supplierIndex].moduleType}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              fullWidth
            >
              {moduleTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Cell Size"
              name="cellSize"
              value={formData[supplierIndex].cellSize}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              fullWidth
            />
            <TextField
              label="Cell Type"
              name="cellType"
              value={formData[supplierIndex].cellType}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              fullWidth
            />
            <TextField
              label="Number of Cells"
              name="numberOfCells"
              value={formData[supplierIndex].numberOfCells}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              fullWidth
            />
            <TextField
              label="Module Size"
              name="moduleSize"
              value={formData[supplierIndex].moduleSize}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              fullWidth
            />
            <TextField
              label="Mounting Hole Location"
              name="mountingHoles"
              value={formData[supplierIndex].mountingHoles}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              fullWidth
            />
            <TextField
              label="Module Technology"
              name="moduleTechnology"
              value={formData[supplierIndex].moduleTechnology}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              fullWidth
            />
            <TextField
              label="Number of Module Ratings"
              name="moduleRatings"
              type="number"
              InputProps={{ inputProps: { min: 1, max: 5 } }}
              value={formData[supplierIndex].moduleRatings}
              onChange={(e) => handleInputChange(supplierIndex, e)}
              onBlur={(e) => handleRatingBlur(supplierIndex, e)}
              fullWidth
            />
          </Box>
          {formData[supplierIndex].moduleWattageData.map((wattageData, wattageIndex) => (
            <Box key={wattageIndex} mt={4} p={2} border="1px solid #ccc" borderRadius="8px">
              <Typography variant="subtitle1" gutterBottom>
                {`Module Wattage ${wattageIndex + 1}`}
              </Typography>
              <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
                <TextField
                  label="Module Wattage (Wp)"
                  name="moduleWattageWp"
                  value={wattageData.moduleWattageWp}
                  onChange={(e) => handleWattageChange(supplierIndex, wattageIndex, e)}
                  fullWidth
                />
                <TextField
                  label="Module Wattage (Nos)"
                  name="moduleWattageNos"
                  value={wattageData.moduleWattageNos}
                  onChange={(e) => handleWattageChange(supplierIndex, wattageIndex, e)}
                  fullWidth
                />
                <TextField
                  select
                  label="Binning Option"
                  name="binningOption"
                  value={wattageData.binningOption}
                  onChange={(e) => handleWattageChange(supplierIndex, wattageIndex, e)}
                  fullWidth
                >
                  {binningOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {renderBinningFields(wattageData.binningOption, wattageData, (e) =>
                handleWattageChange(supplierIndex, wattageIndex, e)
              )}
            </Box>
          ))}
          <details
            className="bg-gray-100 p-4 rounded-lg mt-3"
            style={{ boxShadow: 'inset 0px 0px 4px 2px rgb(0 0 0 / 0.05)' }}
          >
            <summary className="text-xl text-[#424752] cursor-pointer">Materials</summary>
            <Box mt={4} p={2} border="1px solid #ccc" borderRadius="8px">
              <Material
                materials={formData[supplierIndex].materials}
                onMaterialsChange={(updatedMaterials) => handleMaterialChange(supplierIndex, updatedMaterials)}
              />
            </Box>
          </details>          
        </Box>
      ))}
    </div>
  );
};

export default StepThree;
